import { Colors, Loading } from '@helloself/hello-self-ui';
import { FC, lazy } from 'react';
import { Redirect, Route, RouteComponentProps, useLocation } from 'react-router-dom';
import { useAppSelector } from './app/hooks';
import { retry } from './utilities/loadableRetry';

/* eslint-disable @typescript-eslint/no-explicit-any */
const customLoadable = (importFn: () => Promise<any>) =>
    lazy(() => retry(importFn, { retries: 3, interval: 500, backoffExponent: 2 }));

const redirectPathIfAuth = {
    '/login': '/dashboard',
    '/refer': '/new-referral',
};

export const LoadingRoute = () => {
    return <Loading color={Colors.MemberGreen700} />;
};

function isValidRedirectKey(key: string): key is keyof typeof redirectPathIfAuth {
    return key in redirectPathIfAuth;
}

export const PublicRoute = ({
    path,
    Component,
    exact,
    ...rest
}: {
    path: string;
    Component: FC<React.PropsWithChildren<RouteComponentProps>>;
    exact?: boolean;
}) => {
    const isAuthenticated = useAppSelector((state) => state.authentication.isAuthenticated);
    if (isAuthenticated === true && isValidRedirectKey(path)) {
        return <Redirect to={redirectPathIfAuth[path]} />;
    }
    return <Route path={path} exact={exact} {...rest} render={(props) => <Component {...props} />} />;
};

export const PrivateRoute = ({
    path,
    Component,
    exact,
    ...rest
}: {
    path: string;
    Component: FC<React.PropsWithChildren<RouteComponentProps>>;
    exact?: boolean;
}) => {
    const location = useLocation();
    const isAuthenticated = useAppSelector((state) => state.authentication.isAuthenticated);
    if (isAuthenticated === null) {
        // The app has not initialised fully yet
        return null;
    } else if (isAuthenticated === false) {
        if (location.pathname !== '/') {
            // Save a login redirect if it's not the root path
            localStorage.setItem('login-redirect', location.pathname);
        }
        return <Redirect to="/login" />;
    }
    return <Route path={path} exact={exact} {...rest} render={(props) => <Component {...props} />} />;
};

const LoginRoute: FC<RouteComponentProps> = customLoadable(() => import('./features/auth/LoginRoute'));
const RegisterRoute: FC<RouteComponentProps> = customLoadable(() => import('./features/auth/RegisterRoute'));
const ForgotPasswordRoute: FC<RouteComponentProps> = customLoadable(
    () => import('./features/auth/ForgotPasswordRoute')
);
const ContactRoute: FC<RouteComponentProps> = customLoadable(() => import('./features/contact/ContactRoute'));
const ReferRoute = customLoadable(() => import('./features/refer/containers/ReferRoute'));
const OrgUnitReferralRoute = customLoadable(() => import('./features/orgUnitReferral/containers/OrgUnitReferralRoute'));

export const publicRoutes = [
    { path: '/login', Component: LoginRoute },
    { path: '/register', Component: RegisterRoute },
    { path: '/password/reset', Component: ForgotPasswordRoute },
    { path: '/contact', Component: ContactRoute },
    { path: '/refer', Component: ReferRoute },
    { path: '/org-unit-refer', Component: OrgUnitReferralRoute },
];

const ReportPageRoute: FC<React.PropsWithChildren<RouteComponentProps>> = customLoadable(
    () => import('./features/report/containers/ReportPage/ReportPageRoute')
);

const ReportPageV2Route: FC<React.PropsWithChildren<RouteComponentProps>> = customLoadable(
    () => import('./features/report/containers/ReportPage/ReportPageV2Route')
);

const CamhsConsentRoute: FC<React.PropsWithChildren<RouteComponentProps>> = customLoadable(
    () => import('./features/camhsConsent/CamhsConsentRoute')
);

const DashboardRoute: FC<React.PropsWithChildren<RouteComponentProps>> = customLoadable(
    () => import('./features/dashboard/DashboardRoute')
);

const NewReferralRoute: FC<React.PropsWithChildren<RouteComponentProps>> = customLoadable(
    () => import('./features/newReferral/NewReferralRoute')
);

const MemberPageRoute: FC<React.PropsWithChildren<RouteComponentProps>> = customLoadable(
    () => import('./features/member/containers/MemberPage/MemberPageRoute')
);

const ReferralPageRoute: FC<React.PropsWithChildren<RouteComponentProps>> = customLoadable(
    () => import('./features/member/containers/MemberPage/MemberPageRoute')
);

const ReferralPageV2Route: FC<React.PropsWithChildren<RouteComponentProps>> = customLoadable(
    () => import('./features/member/containers/MemberPage/MemberPageV2Route')
);

const ConnectionsPageRoute: FC<React.PropsWithChildren<RouteComponentProps>> = customLoadable(
    () => import('./features/connections/ConnectionsRoute')
);

const MessagingRoute: FC<React.PropsWithChildren<RouteComponentProps>> = customLoadable(
    () => import('./features/messaging/containers/MessagingRoute')
);

const NewConversationRoute: FC<React.PropsWithChildren<RouteComponentProps>> = customLoadable(
    () => import('./features/messaging/containers/NewConversationRoute')
);

const ProfilePageRoute: FC<React.PropsWithChildren<RouteComponentProps>> = customLoadable(
    () => import('./features/profile/containers/ProfilePageRoute')
);

const CommunityGuidelinesRoute: FC<React.PropsWithChildren<RouteComponentProps>> = customLoadable(
    () => import('./features/communityGuidelines/CommunityGuidelinesRoute')
);

export const privateRoutes = [
    { path: '/report/:reportId', Component: ReportPageRoute },
    { path: '/report-v2/:reportId', Component: ReportPageV2Route },
    { path: '/member/:memberId', Component: MemberPageRoute },
    { path: '/referrals/:referralId', Component: ReferralPageRoute },
    { path: '/referrals-v2/:referralId', Component: ReferralPageV2Route },
    { path: '/dashboard', Component: DashboardRoute },
    { path: '/new-referral', Component: NewReferralRoute },
    { path: '/network', Component: ConnectionsPageRoute },
    { path: '/messages', Component: MessagingRoute, exact: true },
    { path: '/messages/:userType/:cognitoId', Component: NewConversationRoute, exact: true },
    { path: '/profile/:cognitoId', Component: ProfilePageRoute },
    { path: '/community-guidelines', Component: CommunityGuidelinesRoute },
    { path: '/camhs-consent', Component: CamhsConsentRoute },
];
